import { useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { CORE_GROUP_FIELDS } from './graphql/fragments';
import {
  createGroup,
  deleteGroup as deleteGroupGql,
  deleteMemberGroups,
  transferGroup,
  transferUserGroups,
  updateGroupMembers,
  updateGroup,
} from './graphql/mutations';
import { listGroups } from './graphql/queries';

export default function useGroups({
  useGraphQLMutation,
  useLazyGraphQLQuery,
  organizationId,
  registeredMembers,
  setFormStatus,
  setModifyContentStatus,
}) {
  const groupsLimit = 100;

  const { t } = useTranslation();

  const [createGroupFormStatus, setCreateGroupFormStatus] = useState(null);
  const [deleteGroup, setDeleteGroup] = useState(false);
  const [deleteGroupMember, setDeleteGroupMember] = useState(false);
  const [editGroupFormStatus, setEditGroupFormStatus] = useState(null);
  const [editGroupMembersOpen, setEditGroupMembersOpen] = useState(false);
  const [groupIdToRemoveMember, setGroupIdToRemoveMember] = useState();
  const [groupToDelete, setGroupToDelete] = useState(null);
  const [memberToDeleteFromGroup, setMemberToDeleteFromGroup] = useState(null);
  const [transferGroupStatus, setTransferGroupStatus] = useState(null);
  const [updateGroupMembersStatus, setUpdateGroupMembersStatus] =
    useState(null);

  const [
    listGroupsQuery,
    { fetchMore: listGroupsFetchMore, ...listGroupsResponse },
  ] = useLazyGraphQLQuery(listGroups);

  const [createGroupMutation] = useGraphQLMutation(createGroup, {
    onCompleted: () => {
      setCreateGroupFormStatus({
        success: true,
        message: t('administrateUsers.createGroupSuccess'),
      });
    },
    onError: () => {
      setCreateGroupFormStatus({
        success: false,
        message: t('administrateUsers.createGroupfailed'),
      });
    },
    update(cache, { data: { createGroup } }) {
      cache.modify({
        fields: {
          // eslint-disable-next-line default-param-last
          listGroups(existingGroups = [], { canRead }) {
            const newGroupRef = cache.writeFragment({
              data: createGroup,
              fragment: CORE_GROUP_FIELDS,
              fragmentName: 'CoreGroupFields',
            });
            return [...existingGroups.filter(canRead), newGroupRef];
          },
        },
      });
    },
  });

  const [deleteGroupMutation] = useGraphQLMutation(deleteGroupGql, {
    update(cache, { data: { deleteGroup } }) {
      cache.evict({ id: cache.identify(deleteGroup) });
      cache.gc();
    },
    onCompleted: () => {
      setFormStatus({
        success: true,
        message: t('administrateUsers.deleteGroupSuccess'),
      });
    },
    onError: () => {
      setFormStatus({
        success: false,
        message: t('administrateUsers.deleteGroupFailure'),
      });
    },
  });

  const [deleteMemberGroupsMutation] = useGraphQLMutation(deleteMemberGroups, {
    update(cache, { data: { deleteMemberGroups } }) {
      deleteMemberGroups.forEach((deletedGroup) => {
        cache.evict({ id: cache.identify(deletedGroup) });
      });
      cache.gc();
    },
    onCompleted: () => {
      setModifyContentStatus((currentStatus) => ({
        ...currentStatus,
        groups: true,
      }));
    },
    onError: () => {
      setModifyContentStatus((currentStatus) => ({
        ...currentStatus,
        error: true,
      }));
    },
  });

  const [transferUserGroupsMutation] = useGraphQLMutation(transferUserGroups, {
    onCompleted: () => {
      setModifyContentStatus((currentStatus) => ({
        ...currentStatus,
        groups: true,
      }));
    },
    onError: () => {
      setModifyContentStatus((currentStatus) => ({
        ...currentStatus,
        error: true,
      }));
    },
  });

  const [updateGroupMutation] = useGraphQLMutation(updateGroup, {
    onCompleted: () => {
      setEditGroupFormStatus({
        success: true,
      });
    },
    onError: () => {
      setEditGroupFormStatus({
        success: false,
      });
    },
  });

  const [updateGroupMembersMutation, updateGroupMembersResponse] =
    useGraphQLMutation(updateGroupMembers, {
      onCompleted: () => {
        setUpdateGroupMembersStatus({
          success: true,
          message: t('administrateUsers.groupUpdatedSuccessMessage'),
        });
        setEditGroupMembersOpen(false);
      },
      onError: () => {
        setUpdateGroupMembersStatus({
          success: false,
          message: t('administrateUsers.updateGroupMembersFailed'),
        });
      },
    });

  const [transferGroupMutation] = useGraphQLMutation(transferGroup, {
    onCompleted: () => {
      setTransferGroupStatus({
        success: true,
        message: t('administrateUsers.transferredOwnershipSuccess'),
      });
      setEditGroupMembersOpen(false);
    },
    onError: () => {
      setTransferGroupStatus({
        success: false,
        message: t('administrateUsers.transferredOwnershipFailure'),
      });
    },
  });

  const getGroupsInfo = () => {
    listGroupsQuery({
      variables: {
        filter: {
          organizationId: {
            eq: organizationId,
          },
        },
        pagination: { limit: groupsLimit, offset: 0 },
      },
    });
  };

  const groupsInfo = listGroupsResponse?.data?.listGroups || [];

  const handleUpdateGroupMembers = (groupMembers, groupId) => {
    const userIds = groupMembers
      .filter((member) => !member.isInvited)
      .map((member) => member.id);

    const invitationIds = groupMembers
      .filter((member) => member.isInvited)
      .map((member) => member.invitationId);

    updateGroupMembersMutation({
      variables: {
        input: {
          id: groupId,
          organizationId,
          userIds,
          invitationIds,
        },
      },
    });
  };

  const onCreateGroup = (values) => {
    createGroupMutation({
      variables: {
        input: {
          ...values,
          organizationId,
        },
      },
    });
  };

  const onDeleteGroup = (group) => {
    setDeleteGroup(true);
    setGroupToDelete(group);
  };

  const onDeleteGroupConfirm = () => {
    const { id } = groupToDelete;

    deleteGroupMutation({
      variables: { id, organizationId },
    });
  };

  const onDeleteGroupMember = (user, groupId) => {
    setUpdateGroupMembersStatus(null);
    setDeleteGroupMember(true);
    setMemberToDeleteFromGroup(user);
    setGroupIdToRemoveMember(groupId);
  };

  const onDeleteGroupMemberConfirm = () => {
    const group = groupsInfo.find(
      (group) => group.id === groupIdToRemoveMember
    );

    if (!group) {
      setUpdateGroupMembersStatus({
        success: false,
        message: t('administrateUsers.updateGroupMembersFailed'),
      });
      return;
    }

    const registeredUsers = group.members.reduce((users, member) => {
      if (member.user.id !== memberToDeleteFromGroup.id) {
        users.push(member.user);
      }

      return users;
    }, []);

    const invitedUsers = (group.pendingInvitations || []).reduce(
      (invitations, invitation) => {
        if (invitation.id !== memberToDeleteFromGroup.invitationId) {
          invitations.push({
            id: `invitation-${invitation.id}`,
            invitationId: invitation.id,
            isInvited: true,
            name: invitation.name,
            email: invitation.email,
          });
        }

        return invitations;
      },
      []
    );

    handleUpdateGroupMembers(
      [...registeredUsers, ...invitedUsers],
      groupIdToRemoveMember
    );
  };

  const onSaveEditGroup = ({ groupId, name, description }) => {
    updateGroupMutation({
      variables: { input: { description, id: groupId, name, organizationId } },
    });
  };

  const onTransferGroup = (groupId, newOwnerId) => {
    transferGroupMutation({
      variables: {
        input: {
          groupId,
          newOwnerId,
          organizationId,
        },
      },
    });
  };

  const requestToDeleteMemberGroups = (memberId) => {
    deleteMemberGroupsMutation({ variables: { memberId, organizationId } });
  };

  const requestToTransferUserGroups = (values) => {
    const { memberId, contentTransferTo: newOwnerId } = values;

    const currentOwner = registeredMembers.find(
      (member) => member.id === memberId
    ).user.id;

    transferUserGroupsMutation({
      variables: {
        input: {
          currentOwner,
          newOwnerId,
          organizationId,
        },
      },
    });
  };

  useEffect(() => {
    if (organizationId) {
      getGroupsInfo();
    }
  }, [organizationId]);

  // pagination for listGroups
  useEffect(() => {
    if (
      !listGroupsResponse.loading &&
      listGroupsResponse.data &&
      listGroupsResponse.data.listGroups.length > 0 &&
      listGroupsResponse.data.listGroups.length % groupsLimit === 0
    ) {
      listGroupsFetchMore({
        variables: {
          pagination: {
            limit: groupsLimit,
            offset: listGroupsResponse.data.listGroups.length,
          },
        },
      });
    }
  }, [listGroupsResponse.loading, listGroupsResponse.data]);

  return {
    createGroupFormStatus,
    deleteGroup,
    deleteGroupMember,
    editGroupFormStatus,
    editGroupMembersOpen,
    getGroupsInfo,
    groupsInfo,
    loadingGroupsInfo: listGroupsResponse.loading,
    loadingUpdateGroupMembers: updateGroupMembersResponse.loading,
    onCreateGroup,
    onDeleteGroup,
    onDeleteGroupConfirm,
    onDeleteGroupMember,
    onDeleteGroupMemberConfirm,
    onSaveEditGroup,
    onTransferGroup,
    requestToDeleteMemberGroups,
    requestToTransferUserGroups,
    setCreateGroupFormStatus,
    setDeleteGroup,
    setDeleteGroupMember,
    setEditGroupFormStatus,
    setEditGroupMembersOpen,
    setMemberToDeleteFromGroup,
    setTransferGroupStatus,
    setUpdateGroupMembersStatus,
    transferGroupStatus,
    updateGroupMembers: handleUpdateGroupMembers,
    updateGroupMembersResponse,
    updateGroupMembersStatus,
  };
}
